import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useMediaQuery } from 'react-responsive';
import 'react-circular-progressbar/dist/styles.css';
import 'react-phone-input-2/lib/style.css';

import Text from 'common/components/Text/Text';
import {
  DEGREE_TYPES,
  SUBJECT_TYPES,
  MONTHS,
  RESIDENT_COUNTRIES,
  GPASCALE,
  GPASCALEPOSITION,
  GPASCALEVALUES,
} from 'common/constants';
import { CommonHash } from 'app/types';
import styles from './UserInformation.module.scss';
import UserFileUpload from './UserFileUpload';

const locationOptions = RESIDENT_COUNTRIES.map((v: string, i: number) => ({ label: v, value: v }));
const degreeTypeOptions = DEGREE_TYPES.map(([label, value]) => ({
  label: label,
  value: value,
}));

const subjectTypeOptions = SUBJECT_TYPES.map(([label, value]) => ({
  label: label,
  value: value,
}));

const gpaScaleOptions = GPASCALE.map(([value, label]) => ({
  label: label,
  value: value,
}));

const transformGPAOptions = (gpaScale: string) => {
  const array: any = [];
  GPASCALEVALUES.forEach((v: any) => {
    array.push({ label: v[gpaScale as keyof typeof GPASCALEPOSITION], value: v[gpaScale] });
  });
  return array;
};

const currentYear: number = new Date().getFullYear();
const years = [];
for (let i = currentYear; i >= currentYear - 60; i--) {
  years.push(i);
}
const yearsOptions = years.map((v: number, i: number) => ({ label: v, value: v }));

const monthsOptions = MONTHS.map((v: string, i: number) => ({ label: v, value: i + 1 }));

interface CompProps {
  index: number;
  answers: CommonHash;
  setAnswer: (h: CommonHash) => void;
}

const AcademicForm = ({ index, answers, setAnswer }: CompProps): React.ReactElement => {
  const [historyAnswers, setHistoryAnswers] = useState<CommonHash>({});
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [gpaScale, setGPAScale] = useState<string>();
  const [gpaValuesAvailableOptions, setGPAValuesAvailableOptions] = useState<any[]>([]); // cambiar esto

  const setInputOption = (key: string, value: any) => {
    setHistoryAnswers((a: any) => ({ ...a, [key]: value }));
  };
  const degreeTypeLabel = () => {
    const value = degreeTypeOptions.find((o: any) => o.value === historyAnswers['degree_type']);
    if (value) return value;
    return historyAnswers['degree_type'] !== undefined
      ? { label: 'Other degree', value: '' }
      : null;
  };

  useEffect(() => {
    if (answers['academic_history'] && answers['academic_history'][index]) {
      setHistoryAnswers(answers['academic_history'][index]);
      setGPAScale(answers['academic_history'][index]['gpa_scale']);
    }
  }, []);

  useEffect(() => {
    if (answers['academic_history']) {
      const list = [...answers['academic_history']];
      list.splice(index, 1, historyAnswers);
      setAnswer((a: any) => ({
        ...a,
        ['academic_history']: list,
      }));
    }
  }, [historyAnswers, setAnswer]);

  useEffect(() => {
    if (gpaScale) {
      setGPAValuesAvailableOptions(transformGPAOptions(gpaScale));
    }
  }, [gpaScale]);

  return (
    <div className={styles.inputsContainter}>
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          School name
        </Text>
        <input
          className="form-control"
          type="text"
          onChange={(e) => setInputOption('school_name', e.target.value)}
          value={historyAnswers.school_name ? historyAnswers.school_name : ''}
        />
      </div>
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          Degree type
        </Text>
        <Select
          isSearchable
          classNamePrefix="react-select"
          className={styles.selectInput}
          options={degreeTypeOptions}
          value={degreeTypeLabel()}
          onChange={({ value }: any) => setInputOption('degree_type', value)}
        />
        {!['high_school', 'bachelors_degree', 'masters_degree', 'mba', 'phd'].includes(
          historyAnswers['degree_type']
        ) &&
          historyAnswers['degree_type'] !== undefined && (
            <div>
              <div className={styles.statementQuestion} style={{ marginTop: '40px' }}>
                Type Degree name
              </div>
              <input
                className={`form-control`}
                type="text"
                onChange={(e) => {
                  setInputOption('degree_type', e.target.value);
                }}
                value={
                  historyAnswers['degree_type'] === 'other' ? '' : historyAnswers['degree_type']
                }
                placeholder="Degree name"
              />
            </div>
          )}
      </div>
      <div className={styles.inputGroup}>
        <div className={styles.rowGroup}>
          <Text
            size={isMobile ? 12 : 16}
            weight={isMobile ? 600 : 500}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            GPA Scale
          </Text>
        </div>
        <Select
          isSearchable
          classNamePrefix="react-select"
          className={styles.selectInput}
          options={gpaScaleOptions}
          value={gpaScaleOptions.find((o: any) => o.value === historyAnswers['gpa_scale'])}
          onChange={({ value }: any) => {
            setInputOption('gpa_scale', value);
            setGPAScale(value);
          }}
        />
      </div>
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          GPA Score
        </Text>
        {gpaScale === 'grade' || gpaScale === 'ukScale' ? (
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={gpaValuesAvailableOptions}
            value={gpaValuesAvailableOptions.find(
              (o: any) => o.value === String(historyAnswers.grade)
            )}
            onChange={({ label, value }: any) => {
              setInputOption('grade', value);
            }}
          />
        ) : (
          <input
            className="form-control"
            type="number"
            onChange={(e) => setInputOption('grade', e.target.value)}
            value={historyAnswers.grade ? historyAnswers.grade : ''}
          />
        )}
      </div>
      <div className={styles.inputGroup}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          Subject type
        </Text>
        <Select
          isSearchable
          classNamePrefix="react-select"
          className={styles.selectInput}
          options={subjectTypeOptions}
          value={subjectTypeOptions.find((o: any) => o.value === historyAnswers['subject_type'])}
          onChange={({ value }: any) => setInputOption('subject_type', value)}
        />
      </div>
      <div className={styles.inputGroupRow}>
        <div className={styles.inputGroup} style={{ width: '48%' }}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Start year
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={yearsOptions}
            value={yearsOptions.find((o: any) => o.value === historyAnswers['academic_start_year'])}
            onChange={({ value }: any) => setInputOption('academic_start_year', value)}
          />
        </div>
        <div className={styles.inputGroup} style={{ width: '48%' }}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Start month
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={monthsOptions}
            value={monthsOptions.find(
              (o: any) => o.value === historyAnswers['academic_start_month']
            )}
            onChange={({ value }: any) => setInputOption('academic_start_month', value)}
          />
        </div>
      </div>
      <div className={styles.inputGroupRow}>
        <div className={styles.inputGroup} style={{ width: '48%' }}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Finish year
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={yearsOptions}
            value={yearsOptions.find(
              (o: any) => o.value === historyAnswers['academic_finish_year']
            )}
            onChange={({ value }: any) => setInputOption('academic_finish_year', value)}
          />
        </div>
        <div className={styles.inputGroup} style={{ width: '48%' }}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Finish month
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={monthsOptions}
            value={monthsOptions.find(
              (o: any) => o.value === historyAnswers['academic_finish_month']
            )}
            onChange={({ value }: any) => setInputOption('academic_finish_month', value)}
          />
        </div>
      </div>
      <div className={styles.inputGroup}>
        <Text
          size={isMobile ? 12 : 16}
          weight={isMobile ? 600 : 500}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          Degree Location
        </Text>
        <Select
          isSearchable
          classNamePrefix="react-select"
          className={styles.selectInput}
          options={locationOptions}
          placeholder="Select degree location"
          value={locationOptions.find((o: any) => o.value === historyAnswers['degree_location'])}
          onChange={({ value }: any) => setInputOption('degree_location', value)}
        />
      </div>
      <UserFileUpload
        setInputOptionOfForm={setInputOption}
        fileType="transcripts"
        answers={answers['academic_history'][index]}
        index={index}
      />
      <UserFileUpload
        setInputOptionOfForm={setInputOption}
        fileType="degree_certificate"
        answers={answers['academic_history'][index]}
        index={index}
      />
    </div>
  );
};

export default AcademicForm;
